import { CommonModule } from '@angular/common';
import { Component, effect, inject, type OnInit, type Signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { EMPTY } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import type { ILeiProfile } from '@core/models/app.model';
import { getCompanyNameFromLei } from '@helpers/get-compnay-name-from-lei';
import { historyBack } from '@helpers/prev-page-navigation';
import { DisableUntilResolveDirective } from '@shared/directives/disable-until-resolve/disable-until-resolve.directive';
import { EntityNameInputComponent } from '@shared/forms/entity-name-input/entity-name-input.component';
import { getAddressFromLei } from '@shared/forms/helpers';
import { SequentialUpdateBuffer } from '@shared/rx/sequential-update-buffer';
import { LEIDatabaseService } from '@shared/services/lei-database.service';
import { UmeLoaderComponent } from '@widgets/ume-loader/ume-loader.component';
import { UploadLogoComponent } from '@widgets/upload-logo/upload-logo.component';

import { CompanyDetailsBase } from '../../classes/company-details-base.class';
import { type CompanyProfileMeta, PROFILE_STATUS } from '../../company-profile.model';
import {
  OwnPublicProfileState,
  provideOwnPublicProfileState,
} from '../../state/own-public-profile.state';
import { PROFILE_SAVE_COOLDOWN_MS } from '../../state/public-profile.state';

const PROFILE_FIELDS = {
  NAME: 'companyName',
  ABOUT: 'about',
} as const;

@Component({
  selector: 'ume-vp-account-details',
  templateUrl: './vp-account-details.component.html',
  styleUrls: ['./vp-account-details.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltip,
    MatFormField,
    MatInput,
    MatLabel,
    DisableUntilResolveDirective,
    UploadLogoComponent,
    UmeLoaderComponent,
    EntityNameInputComponent,
  ],
  providers: [provideOwnPublicProfileState()],
})
export class VpAccountDetailsComponent extends CompanyDetailsBase implements OnInit {
  readonly fields = PROFILE_FIELDS;

  private readonly leiDatabase: LEIDatabaseService = inject(LEIDatabaseService);
  private readonly _ownState = inject(OwnPublicProfileState);

  /** Name and about are written directly, outside the profile state, so typing is never re-patched. */
  private readonly _profileUpdates: SequentialUpdateBuffer = new SequentialUpdateBuffer(
    PROFILE_SAVE_COOLDOWN_MS
  );

  public readonly profile: Signal<CompanyProfileMeta | null> = this.state.profile;

  public form: FormGroup = new FormGroup({
    [PROFILE_FIELDS.NAME]: new FormControl(null, Validators.required),
    [PROFILE_FIELDS.ABOUT]: new FormControl(null),
  });

  constructor() {
    super();

    this._destroyRef.onDestroy(() => this._profileUpdates.destroy());

    // Re-patch name/about only when the server copy replaces the profile: local patches (logo)
    // must not overwrite what the user typed, while an undo must even if nothing else changed.
    effect(() => {
      this.state.serverRevision();

      untracked(() => {
        const profile = this.profile();

        if (profile) {
          this._applyProfile(profile);
        }
      });
    });

    // Lock the fields from the Submit/Undo click; a failed request restores the profile's form status.
    effect(() => {
      const busy = this.state.isBusy();

      untracked(() => {
        const profile = this.profile();

        if (busy) {
          this.form.disable({ emitEvent: false });
        } else if (profile) {
          this._applyFormStatus(profile);
        }
      });
    });
  }

  ngOnInit(): void {
    this.form
      .get(PROFILE_FIELDS.NAME)
      ?.valueChanges?.pipe(
        debounceTime(PROFILE_SAVE_COOLDOWN_MS),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((companyName) => {
        this._profileUpdates.push(PROFILE_FIELDS.NAME, () => {
          return this.isUpdating() || this.isDraft()
            ? this.profileService.updateCompanyProfileField(PROFILE_FIELDS.NAME, companyName)
            : EMPTY;
        });
      });

    this.form
      .get(PROFILE_FIELDS.ABOUT)
      ?.valueChanges?.pipe(
        debounceTime(PROFILE_SAVE_COOLDOWN_MS),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((about: string) => {
        this._profileUpdates.push(PROFILE_FIELDS.ABOUT, () => {
          return this.profileService.updateCompanyAbout(about || '');
        });
      });
  }

  public undo(): void {
    if (this.state.isBusy()) {
      return;
    }

    this._ownState
      .undo()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.notify.notifySuccess('Changes have been undone');
      });
  }

  public goBack(): void {
    historyBack();
  }

  public selectLei(profile: ILeiProfile): void {
    getAddressFromLei(this.leiDatabase)(profile)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        const newName = getCompanyNameFromLei(profile);
        const patch: any = {};

        if (newName !== this.form.value.companyName) {
          patch[PROFILE_FIELDS.NAME] = newName;
        }

        this.form.patchValue(patch, { emitEvent: false });
      });
  }

  private _applyProfile(profile: CompanyProfileMeta): void {
    this.form.patchValue(
      {
        [PROFILE_FIELDS.NAME]: profile.identity.companyName ?? null,
        [PROFILE_FIELDS.ABOUT]: profile.about ?? null,
      },
      { emitEvent: false }
    );

    // The lock effect restores the status once the request settles.
    if (!this.state.isBusy()) {
      this._applyFormStatus(profile);
    }
  }

  private _applyFormStatus(profile: CompanyProfileMeta): void {
    if (profile.logicalStatus === PROFILE_STATUS.VALIDATED) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
    this.isFormValid.set(this.form.valid);
  }
}
